import { useState } from 'react';
import { IonButton, IonIcon, IonInput, IonItem, IonLabel, IonList, IonRow } from '@ionic/react';
import { pencil } from 'ionicons/icons';
import styles from './InlineTypePicker.module.scss';
import { useTranslation } from 'react-i18next';

// The contents of a type picker's dropdown card: the fixed type rows plus one
// custom row that opens a rename box. Shared by InlineTypePicker (email /
// address) and PhoneAgentInput so every type picker looks and behaves the same.
//
// Rendered inside PortalPickerDropdown, which unmounts its children when the
// card closes — so the rename state here resets on close without any wiring.

export interface TypeMenuOption {
  value: string;
  label: string;
  icon: string;
}

interface Props {
  types: TypeMenuOption[];
  /** Current type tag. Matched case-insensitively against `types`. */
  currentType: string;
  /** Called with a chosen type's label (e.g. "Mobile") or a custom string. */
  onSelect: (type: string) => void;
  onClose: () => void;
}

const TypePickerMenu: React.FC<Props> = ({ types, currentType, onSelect, onClose }) => {
  const { t: tr } = useTranslation();
  const [renaming, setRenaming] = useState(false);
  const [customValue, setCustomValue] = useState('');

  const normalized = currentType.toLowerCase();
  const known = types.find(t => t.value === normalized);
  const isCustom = !!normalized && !known && normalized !== 'other';

  const saveCustom = () => {
    if (customValue.trim()) onSelect(customValue.trim());
  };

  if (renaming) {
    return (
      <IonList lines="none" className={styles.renameBox}>
        <IonItem lines="none" className={styles.renameInputItem}>
          <IonInput
            className={styles.renameInput}
            value={customValue}
            placeholder={tr('components.TypePickerMenu.enterCustomType')}
            autofocus
            onIonInput={e => setCustomValue(e.detail.value ?? '')}
          />
        </IonItem>
        <IonRow className={styles.renameActionsRow}>
          <IonButton
            className={`${styles.pillButton} ${styles.pillButtonSecondary}`}
            onClick={onClose}
          >{tr('components.TypePickerMenu.cancel')}
          </IonButton>
          <IonButton
            className={`${styles.pillButton} ${styles.pillButtonPrimary}`}
            onClick={saveCustom}
          >{tr('components.TypePickerMenu.save')}
          </IonButton>
        </IonRow>
      </IonList>
    );
  }

  return (
    <IonList lines="none" className={styles.dropdownList}>
      {types.map(t => {
        const active = normalized === t.value;
        return (
          <IonItem
            key={t.value}
            button
            detail={false}
            lines="none"
            className={`${styles.dropdownItem} ${active ? styles.dropdownItemActive : ''}`}
            onClick={() => onSelect(t.label)}
          >
            <IonIcon slot="start" icon={t.icon} color={active ? 'primary' : undefined} />
            <IonLabel color={active ? 'primary' : undefined}>{t.label}</IonLabel>
          </IonItem>
        );
      })}
      {/* One custom option. Shows "Other" by default, the custom name once set.
          Tapping it opens the rename box (empty for a fresh "Other", prefilled
          when editing an existing custom name). */}
      <IonItem
        button
        detail={false}
        lines="none"
        className={`${styles.dropdownItem} ${isCustom ? styles.dropdownItemActive : ''}`}
        onClick={() => {
          setCustomValue(isCustom ? currentType : '');
          setRenaming(true);
        }}
      >
        <IonIcon slot="start" icon={pencil} color={isCustom ? 'primary' : undefined} />
        <IonLabel color={isCustom ? 'primary' : undefined}>
          {isCustom ? currentType : tr('components.TypePickerMenu.other')}
        </IonLabel>
      </IonItem>
    </IonList>
  );
};

export default TypePickerMenu;
